<template>
        <div class="min-h-screen bg-gray-50 font-sans">
                <!-- Header -->
                <header class="bg-white border-b border-gray-200 py-4 px-3">
                        <div class="w-full flex justify-between items-center flex-wrap gap-2">
                                <div>
                                        <button
                                                class="inline-flex items-center gap-1 text-orange-7 bg-transparent border-none cursor-pointer text-sm p-0 mb-2 hover:text-orange-6 hover:underline"
                                                @click="goBack"
                                        >
                                                <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                                                </svg>
                                                Back to Products Ordered
                                        </button>
                                        <h1 class="text-2xl font-semibold text-gray-900 m-0">Order Item Details</h1>
                                        <p class="text-sm text-gray-500 mt-1 mb-0">Order Item #{{ routeId }}</p>
                                </div>
                        </div>
                </header>

                <!-- Main Content -->
                <main class="w-full p-3">
                        <!-- Loading State -->
                        <div v-if="isLoading" class="flex justify-center items-center py-16 px-4">
                                <svg class="w-10 h-10 text-orange-7 animate-spin" fill="none" viewBox="0 0 24 24">
                                        <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                                        <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                </svg>
                                <span class="ml-3 text-gray-500 text-sm">Loading order item...</span>
                        </div>

                        <div v-else class="flex flex-col gap-3">
                                <!-- Order Item Info Card -->
                                <div class="bg-white rounded-xl shadow-sm overflow-hidden">
                                        <div class="py-4 px-6 border-b border-gray-200 bg-gray-50">
                                                <h2 class="text-lg font-semibold text-gray-900 m-0">Order Info</h2>
                                        </div>
                                        <div class="p-6">
                                                <div class="grid grid-cols-[repeat(auto-fit,minmax(250px,1fr))] gap-4">
                                                        <div class="flex justify-between py-2 border-b border-gray-200">
                                                                <span class="text-gray-500 text-sm">Order Item ID</span>
                                                                <span class="font-medium text-gray-900 text-sm">{{ orderItem.id }}</span>
                                                        </div>
                                                        <div class="flex justify-between py-2 border-b border-gray-200">
                                                                <span class="text-gray-500 text-sm">External ID</span>
                                                                <span class="text-gray-700 text-sm">{{ orderItem.externalOrderId || '-' }}</span>
                                                        </div>
                                                        <div class="flex justify-between py-2 border-b border-gray-200">
                                                                <span class="text-gray-500 text-sm">Order ID</span>
                                                                <button
                                                                        class="text-orange-7 bg-transparent border-none cursor-pointer text-sm p-0 hover:underline"
                                                                        @click="goToOrder(orderItem.orderid!)"
                                                                >
                                                                        {{ orderItem.orderid }}
                                                                </button>
                                                        </div>
                                                        <div class="flex justify-between py-2 border-b border-gray-200">
                                                                <span class="text-gray-500 text-sm">Offering ID</span>
                                                                <button
                                                                        class="text-orange-7 bg-transparent border-none cursor-pointer text-sm p-0 hover:underline"
                                                                        @click="goToOffering(orderItem.offeringid!)"
                                                                >
                                                                        {{ orderItem.offeringid }}
                                                                </button>
                                                        </div>
                                                        <div class="flex justify-between py-2 border-b border-gray-200">
                                                                <span class="text-gray-500 text-sm">Product ID</span>
                                                                <a
                                                                        :href="productUrl"
                                                                        target="_blank"
                                                                        class="text-orange-7 no-underline text-sm hover:underline"
                                                                >
                                                                        {{ orderItem.productid }}
                                                                </a>
                                                        </div>
                                                        <div class="flex justify-between py-2 border-b border-gray-200">
                                                                <span class="text-gray-500 text-sm">Offering Name</span>
                                                                <span class="text-gray-700 text-sm">{{ offeringData.name }} {{ offeringData.description }} ({{ offeringData.variantname }})</span>
                                                        </div>
                                                        <div class="flex justify-between py-2 border-b border-gray-200">
                                                                <span class="text-gray-500 text-sm">Number of Pages</span>
                                                                <span class="text-gray-700 text-sm">{{ orderItem.pages }}</span>
                                                        </div>
                                                        <div class="flex justify-between py-2 border-b border-gray-200">
                                                                <span class="text-gray-500 text-sm">Order Quantity</span>
                                                                <span class="text-gray-700 text-sm">{{ orderItem.quantity }}</span>
                                                        </div>
                                                        <div class="flex justify-between py-2 border-b border-gray-200">
                                                                <span class="text-gray-500 text-sm">Shipping Price</span>
                                                                <span class="text-gray-700 text-sm">{{ orderItem.price_shipping }}</span>
                                                        </div>
                                                        <div class="flex justify-between py-2 border-b border-gray-200">
                                                                <span class="text-gray-500 text-sm">Handling Price</span>
                                                                <span class="text-gray-700 text-sm">{{ orderItem.price_handling }}</span>
                                                        </div>
                                                        <div class="flex justify-between py-2 border-b border-gray-200">
                                                                <span class="text-gray-500 text-sm">Product Price</span>
                                                                <span class="text-gray-700 text-sm">{{ orderItem.price_product }}</span>
                                                        </div>
                                                        <div class="flex justify-between py-2 border-b border-gray-200">
                                                                <span class="text-gray-500 text-sm">Voucher Discount</span>
                                                                <span class="text-gray-700 text-sm">{{ orderItem.discount_voucher }}</span>
                                                        </div>
                                                        <div class="flex justify-between py-2 border-b border-gray-200">
                                                                <span class="text-gray-500 text-sm">Total Price</span>
                                                                <span class="font-semibold text-gray-900 text-sm">{{ orderItem.salesvalue }}</span>
                                                        </div>
                                                        <div class="flex justify-between py-2 border-b border-gray-200">
                                                                <span class="text-gray-500 text-sm">Product VAT</span>
                                                                <span class="text-gray-700 text-sm">{{ orderItem.vat }}</span>
                                                        </div>
                                                        <div class="flex justify-between py-2 border-b border-gray-200">
                                                                <span class="text-gray-500 text-sm">Tracker ID</span>
                                                                <span class="text-gray-700 text-sm">{{ orderItem.affiliateid || '-' }}</span>
                                                        </div>
                                                </div>
                                        </div>
                                </div>

                                <!-- Order Item Status Card -->
                                <div class="bg-white rounded-xl shadow-sm overflow-hidden">
                                        <div class="py-4 px-6 border-b border-gray-200 bg-gray-50">
                                                <h2 class="text-lg font-semibold text-gray-900 m-0">Order Item Status</h2>
                                        </div>
                                        <div class="overflow-x-auto">
                                                <table class="w-full border-collapse">
                                                        <thead>
                                                                <tr class="bg-gray-50 border-b border-gray-200">
                                                                        <th class="py-3 px-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Status</th>
                                                                        <th class="py-3 px-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Tracking Code</th>
                                                                        <th class="py-3 px-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Tracking Link</th>
                                                                        <th class="py-3 px-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Carrier</th>
                                                                        <th class="py-3 px-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Ship Date</th>
                                                                        <th class="py-3 px-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Earliest Delivery</th>
                                                                        <th class="py-3 px-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Latest Delivery</th>
                                                                </tr>
                                                        </thead>
                                                        <tbody>
                                                                <tr v-if="orderStatuses.length === 0">
                                                                        <td colspan="7" class="py-8 px-4 text-center text-gray-500">
                                                                                No status records found
                                                                        </td>
                                                                </tr>
                                                                <tr
                                                                        v-for="status in orderStatuses"
                                                                        :key="status.id"
                                                                        class="border-b border-gray-200 transition-colors hover:bg-gray-50"
                                                                >
                                                                        <td class="py-3 px-4 text-sm text-gray-700">{{ status.status }}</td>
                                                                        <td class="py-3 px-4 text-sm text-gray-700">{{ status.trackingcode || '-' }}</td>
                                                                        <td class="py-3 px-4 text-sm">
                                                                                <a
                                                                                        v-if="status.trackinglink"
                                                                                        :href="status.trackinglink"
                                                                                        target="_blank"
                                                                                        class="text-orange-7 no-underline hover:underline"
                                                                                >
                                                                                        View
                                                                                </a>
                                                                                <span v-else class="text-gray-700">-</span>
                                                                        </td>
                                                                        <td class="py-3 px-4 text-sm text-gray-700">{{ status.carrier || '-' }}</td>
                                                                        <td class="py-3 px-4 text-sm text-gray-700">{{ status.shipdate || '-' }}</td>
                                                                        <td class="py-3 px-4 text-sm text-gray-700">{{ status.deliverydate_earliest || '-' }}</td>
                                                                        <td class="py-3 px-4 text-sm text-gray-700">{{ status.deliverydate_latest || '-' }}</td>
                                                                </tr>
                                                        </tbody>
                                                </table>
                                        </div>
                                </div>
                        </div>
                </main>
        </div>
</template>

<script lang="ts">
import { defineComponent, ref, computed, onMounted, Ref, ComputedRef } from 'vue';
import { useRouter, useRoute, Router, RouteLocationNormalizedLoaded } from 'vue-router';
import Swal, { SweetAlertIcon } from 'sweetalert2';
import OrderItemService from 'services/OrderItemService';
import OfferingService from 'services/OfferingService';
import { creatorDomain } from 'utils/http';
import type { OrderItemModel, OrderItemStatusModel, OfferingModel } from '@sosocio/creator/interfaces';

export default defineComponent({
        name: 'OrderItemPage',
        setup() {
                const router: Router = useRouter();
                const route: RouteLocationNormalizedLoaded = useRoute();

                const routeId = computed(() => Number(route.params.orderItemId));
                const orderItem: Ref<Partial<OrderItemModel>> = ref({
                        id: 0,
                        orderid: 0,
                        offeringid: 0,
                        productid: 0,
                });
                const offeringData: Ref<Partial<OfferingModel>> = ref({});
                const orderStatuses: Ref<OrderItemStatusModel[]> = ref([]);
                const isLoading: Ref<boolean> = ref(true);

                const productUrl: ComputedRef<string> = computed(() => {
                        return `${creatorDomain}/app/showcase/${orderItem.value.productid}/${orderItem.value.read_token}`;
                });

                const goBack = (): void => {
                        router.push('/products-ordered');
                };

                const loadData = async (): Promise<void> => {
                        isLoading.value = true;
                        try {
                                const params = new URLSearchParams({ limit: '0' });
                                const itemData = await OrderItemService.get(routeId.value);
                                orderItem.value = itemData;

                                const [offering, statuses] = await Promise.all([
                                        OfferingService.get(itemData.offeringid),
                                        OrderItemService.getAllOrderItemStatus(routeId.value, params),
                                ]);
                                offeringData.value = offering;
                                orderStatuses.value = statuses;
                        } catch (error) {
                                console.error('Failed to load order item:', error);
                                const errorMessage = error instanceof Error ? error.message : 'Failed to load order item';
                                Swal.fire({
                                        title: 'Error',
                                        text: errorMessage,
                                        icon: 'error' as SweetAlertIcon,
                                });
                        } finally {
                                isLoading.value = false;
                        }
                };

                const goToOrder = (orderId: number): void => {
                        router.push(`/showOrder/${orderId}`);
                };

                const goToOffering = (offeringId: number): void => {
                        router.push(`/offerings/${offeringId}`);
                };

                onMounted(() => {
                        loadData();
                });

                return {
                        routeId,
                        orderItem,
                        offeringData,
                        orderStatuses,
                        isLoading,
                        productUrl,
                        goBack,
                        goToOrder,
                        goToOffering,
                };
        },
});
</script>

<style scoped>
@keyframes spin {
        from { transform: rotate(0deg); }
        to { transform: rotate(360deg); }
}
</style>
